// pages/PriceTags.js - таблица ценников на TanStack Table
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import priceTagService from '../services/priceTagService';
import productService from '../services/productService';
import storeService from '../services/storeService';
import { getIcon } from '../utils/icons';
import MultiSelect from '../components/common/MultiSelect';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const PriceTagsPage = () => {
  const navigate = useNavigate();
  const query = useQuery();

  const initialPage = parseInt(query.get('page') || '1', 10);
  const initialStartDate = query.get('startDate') || '';
  const initialEndDate = query.get('endDate') || '';

  const [priceTags, setPriceTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Фильтры
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate
  });

  const categoryOptions = [
    { id: 'Unknown', name: 'Неизвестно' },
    { id: 'Minimum', name: 'Минимальная цена' },
    { id: 'Average', name: 'Средняя цена' },
    { id: 'Maximum', name: 'Максимальная цена' }
  ];

  const hasActiveFilters = selectedProducts.length > 0 ||
                           selectedStores.length > 0 ||
                           selectedCategories.length > 0 ||
                           dateRangeFilter.startDate ||
                           dateRangeFilter.endDate;

  const handleClearFilters = () => {
    setSelectedProducts([]);
    setSelectedStores([]);
    setSelectedCategories([]);
    setDateRangeFilter({ startDate: '', endDate: '' });
  };

  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Модалка просмотра фото
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Сортировка TanStack
  const [sorting, setSorting] = useState([]);

  // Карта presigned URL для миниатюр
  const [imageUrlMap, setImageUrlMap] = useState({});

  const updateUrlParams = (newPage, newFilters) => {
    const params = new URLSearchParams();
    if (newPage > 1) params.set('page', newPage.toString());
    if (newFilters.productId) params.set('productId', newFilters.productId);
    if (newFilters.storeId) params.set('storeId', newFilters.storeId);
    if (newFilters.startDate) params.set('startDate', newFilters.startDate);
    if (newFilters.endDate) params.set('endDate', newFilters.endDate);
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
    // Сохраняем состояние списка, чтобы вернуться сюда из вложенных страниц
    sessionStorage.setItem('priceTagsListUrl', newUrl);
  };

  // Счётчик монтирования — защита от двойного вызова эффектов в StrictMode
  const mountCount = useRef(0);

  useEffect(() => {
    fetchPriceTags(page);
    updateUrlParams(page, {
      productId: selectedProducts.length > 0 ? selectedProducts[0].id : '',
      storeId: selectedStores.length > 0 ? selectedStores[0].id : '',
      startDate: dateRangeFilter.startDate,
      endDate: dateRangeFilter.endDate
    });
  }, [page]);

  useEffect(() => {
    mountCount.current += 1;
    // Пропускаем первые два вызова (монтирование + StrictMode повтор)
    if (mountCount.current <= 2) return;

    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      setPage(1);
      fetchPriceTags(1);
      updateUrlParams(1, {
        productId: selectedProducts.length > 0 ? selectedProducts[0].id : '',
        storeId: selectedStores.length > 0 ? selectedStores[0].id : '',
        startDate: dateRangeFilter.startDate,
        endDate: dateRangeFilter.endDate
      });
    }, 500));
    return () => { if (searchTimeout) clearTimeout(searchTimeout); };
  }, [selectedProducts, selectedStores, selectedCategories, dateRangeFilter.startDate, dateRangeFilter.endDate]);

  // При смене размера страницы — сброс на первую и перезагрузка
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
    fetchPriceTags(1, newSize);
  };

  useEffect(() => {
    fetchProductsAndStores();
  }, []);

  // Загрузка presigned URL для миниатюр при смене данных
  useEffect(() => {
    const loadImageUrls = async () => {
      const newMap = {};
      const promises = priceTags
        .filter(pt => pt.image?.id)
        .map(async (pt) => {
          try {
            const url = await priceTagService.getImagePresignedUrl(pt.image.id);
            newMap[pt.image.id] = url;
          } catch {
            newMap[pt.image.id] = pt.image.filePath;
          }
        });
      await Promise.all(promises);
      setImageUrlMap(prev => ({ ...prev, ...newMap }));
    };
    if (priceTags.length > 0) {
      loadImageUrls();
    }
  }, [priceTags]);

  const fetchProductsAndStores = async () => {
    try {
      const productsPromise = productService.getProducts({ pageSize: 100 });
      const storesPromise = storeService.getStores();
      const [productsResponse, storesResponse] = await Promise.all([productsPromise, storesPromise]);

      let productsData = [];
      if (Array.isArray(productsResponse)) productsData = productsResponse;
      else if (productsResponse?.items) productsData = productsResponse.items;
      else if (productsResponse?.data) productsData = productsResponse.data;

      let storesData = [];
      if (Array.isArray(storesResponse)) storesData = storesResponse;
      else if (storesResponse?.items) storesData = storesResponse.items;
      else if (storesResponse?.data) storesData = storesResponse.data;

      setProducts(productsData);
      setStores(storesData);
    } catch (err) {
      console.error('Ошибка при загрузке данных для фильтров:', err);
      toast.error('Не удалось загрузить фильтры');
    }
  };

  const fetchPriceTags = async (pageNumber, overridePageSize) => {
    setLoading(true);
    try {
      const params = { page: pageNumber, pageSize: overridePageSize || pageSize };

      if (selectedProducts.length > 0) params.productId = selectedProducts[0].id;
      if (selectedStores.length > 0) params.storeId = Number(selectedStores[0].id);
      if (dateRangeFilter.startDate) params.startDate = new Date(dateRangeFilter.startDate);
      if (dateRangeFilter.endDate) {
        const endDate = new Date(dateRangeFilter.endDate);
        endDate.setHours(23, 59, 59, 999);
        params.endDate = endDate;
      }
      if (selectedCategories.length > 0) params.category = selectedCategories[0].id;

      const response = await priceTagService.getPriceTags(params);

      if (response && typeof response === 'object' && response.items) {
        setPriceTags(response.items || []);
        setTotalPages(response.totalPages || 1);
        setTotalCount(response.totalCount || 0);
      } else if (Array.isArray(response)) {
        setPriceTags(response);
        setTotalPages(Math.ceil(response.length / pageSize) || 1);
        setTotalCount(response.length);
      } else if (response && typeof response === 'object') {
        const data = response.data || response;
        setPriceTags(Array.isArray(data) ? data : []);
        setTotalPages(response.totalPages || Math.ceil((response.totalCount || 0) / pageSize) || 1);
        setTotalCount(response.totalCount || (Array.isArray(data) ? data.length : 0));
      } else {
        console.error('Неизвестный формат ответа API:', response);
        setPriceTags([]);
        setTotalPages(1);
        setTotalCount(0);
        toast.error('Ошибка при получении данных');
      }
    } catch (err) {
      console.error('Ошибка при загрузке ценников:', err);
      toast.error('Не удалось загрузить ценники');
      setPriceTags([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePriceTag = () => navigate('/price-tags/new');
  const handleViewPriceTag = (priceTagId) => navigate(`/price-tags/${priceTagId}`);

  const handleVerifyPriceTag = async (priceTagId) => {
    try {
      if (window.confirm('Вы уверены, что хотите верифицировать этот ценник?')) {
        await priceTagService.verifyPriceTag(priceTagId);
        toast.success('Ценник успешно верифицирован');
        fetchPriceTags(page);
      }
    } catch (err) {
      console.error('Ошибка при верификации ценника:', err);
      toast.error('Не удалось верифицировать ценник');
    }
  };

  const handleHidePriceTag = async (priceTagId) => {
    if (window.confirm('Скрыть этот ценник? Он не будет отображаться в списке.')) {
      try {
        await priceTagService.toggleHidden(priceTagId);
        toast.success('Ценник скрыт');
        fetchPriceTags(page);
      } catch (err) {
        console.error('Ошибка при скрытии ценника:', err);
        toast.error('Ошибка при скрытии ценника');
      }
    }
  };

  const handleViewImage = async (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
    if (image && image.id) {
      try {
        const url = await priceTagService.getImagePresignedUrl(image.id);
        setSelectedImageUrl(url);
      } catch (err) {
        console.error('Ошибка получения presigned URL:', err);
        setSelectedImageUrl(image.filePath || image.url);
      }
    } else {
      setSelectedImageUrl(image?.filePath || image?.url);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    try { return new Date(date).toLocaleString('ru-RU'); }
    catch { return date.toString(); }
  };

  // Рендер ячейки цены
  const renderPriceCell = (priceTag) => {
    const price = typeof priceTag.price === 'object' ? priceTag.price?.parsedValue : priceTag.price;
    const discountPrice = typeof priceTag.discountPrice === 'object' ? priceTag.discountPrice?.parsedValue : priceTag.discountPrice;

    // Пересчёт на вес товара
    let recalcPrice = typeof priceTag.recalculatedPriceForProductWeight === 'object'
      ? priceTag.recalculatedPriceForProductWeight?.parsedValue
      : priceTag.recalculatedPriceForProductWeight;

    const productWeight = priceTag.product?.weight
      ? (typeof priceTag.product.weight === 'object' ? priceTag.product.weight?.parsedValue : priceTag.product.weight)
      : null;
    const productWeightUnit = priceTag.product?.weightUnit;

    if (!recalcPrice && priceTag.pricePerUnit && productWeight) {
      const pricePerUnit = typeof priceTag.pricePerUnit === 'object'
        ? priceTag.pricePerUnit?.parsedValue : priceTag.pricePerUnit;
      const weightInKg = productWeightUnit === 'Kilogram' ? productWeight : productWeight / 1000;
      recalcPrice = pricePerUnit * weightInKg;
    }

    if (!recalcPrice && priceTag.originalWeight && productWeight) {
      const originalWeight = typeof priceTag.originalWeight === 'object'
        ? priceTag.originalWeight?.parsedValue : priceTag.originalWeight;
      const originalUnit = priceTag.originalUnit?.toLowerCase() || 'г';
      if (price && originalWeight && originalWeight > 0) {
        const originalWeightInGrams = (originalUnit === 'кг' || originalUnit === 'kg')
          ? originalWeight * 1000 : originalWeight;
        const productWeightInGrams = productWeightUnit === 'Kilogram'
          ? productWeight * 1000 : productWeight;
        recalcPrice = (price / originalWeightInGrams) * productWeightInGrams;
      }
    }

    return (
      <div className="text-sm">
        <span className="text-gray-900 font-medium">{price?.toFixed(2)} ₽</span>
        {discountPrice && (
          <span className="text-red-600 ml-2 line-through">{discountPrice.toFixed(2)} ₽</span>
        )}
        {recalcPrice ? (
          <div className="text-green-600 text-sm mt-1">
            → {recalcPrice.toFixed(2)} ₽
            {productWeight && (
              <span className="text-gray-500 ml-1">
                (за {productWeight}{productWeightUnit === 'Kilogram' ? ' кг' : ' г'})
              </span>
            )}
          </div>
        ) : (
          <div className="text-gray-400 text-sm mt-1">—</div>
        )}
      </div>
    );
  };

  // Определение колонок TanStack Table
  const columns = useMemo(() => [
    {
      accessorFn: (row) => row.product?.name || '',
      id: 'product',
      header: 'Товар',
      cell: ({ row }) => (
        <div className="text-sm font-medium text-gray-900">
          {row.original.product?.name || 'Не указан'}
        </div>
      ),
    },
    {
      accessorFn: (row) => row.store?.name || '',
      id: 'store',
      header: 'Магазин',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {row.original.store?.name || 'Не указан'}
        </div>
      ),
    },
    {
      accessorFn: (row) => {
        const p = row.price;
        return typeof p === 'object' ? p?.parsedValue || 0 : p || 0;
      },
      id: 'price',
      header: 'Цена',
      cell: ({ row }) => renderPriceCell(row.original),
    },
    {
      id: 'photo',
      header: 'Фото',
      enableSorting: false,
      cell: ({ row }) => {
        const image = row.original.image;
        if (!image) {
          return (
            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          );
        }
        const thumbUrl = imageUrlMap[image.id] || image.filePath || image.url;
        return (
          <div
            className="w-12 h-12 rounded overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-gray-100"
            onClick={() => handleViewImage(image)}
            title="Нажмите для увеличения"
          >
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt="Ценник"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-pulse bg-gray-200 w-full h-full rounded"></div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'isVerified',
      header: 'Статус',
      cell: ({ row }) => (
        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
          row.original.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {row.original.isVerified ? 'Верифицирован' : 'Не верифицирован'}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Дата',
      cell: ({ row }) => (
        <div className="text-sm text-gray-500">
          {formatDate(row.original.createdAt)}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Действия',
      enableSorting: false,
      cell: ({ row }) => {
        const priceTag = row.original;
        return (
          <div className="flex space-x-2">
            <button
              onClick={() => handleViewPriceTag(priceTag.id)}
              className="text-indigo-600 hover:text-indigo-900"
              title="Просмотр"
            >
              {getIcon('view')}
            </button>
            {!priceTag.isVerified && (
              <button
                onClick={() => handleVerifyPriceTag(priceTag.id)}
                className="text-green-600 hover:text-green-900"
                title="Верифицировать"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </button>
            )}
            <button
              onClick={() => handleHidePriceTag(priceTag.id)}
              className="text-red-600 hover:text-red-900"
              title="Скрыть ценник"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            </button>
          </div>
        );
      },
    },
  ], [imageUrlMap]);

  const table = useReactTable({
    data: priceTags,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Ценники</h1>
          <p className="text-gray-600">Управление ценниками товаров</p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleCreatePriceTag}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            {getIcon('plus')}
            <span className="ml-2">Загрузить ценник</span>
          </button>
        </div>
      </header>

      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Товары</label>
              <MultiSelect
                options={products}
                selectedValues={selectedProducts}
                onSelectionChange={setSelectedProducts}
                placeholder="Все товары"
                searchPlaceholder="Поиск товара..."
                displayField="name"
                valueField="id"
                maxDisplayItems={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Магазины</label>
              <MultiSelect
                options={stores}
                selectedValues={selectedStores}
                onSelectionChange={setSelectedStores}
                placeholder="Все магазины"
                searchPlaceholder="Поиск магазина..."
                displayField="name"
                valueField="id"
                maxDisplayItems={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Категории цен</label>
              <MultiSelect
                options={categoryOptions}
                selectedValues={selectedCategories}
                onSelectionChange={setSelectedCategories}
                placeholder="Все категории"
                displayField="name"
                valueField="id"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Период</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="flex-1 border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={dateRangeFilter.startDate}
                  onChange={(e) => setDateRangeFilter({...dateRangeFilter, startDate: e.target.value})}
                />
                <input
                  type="date"
                  className="flex-1 border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={dateRangeFilter.endDate}
                  onChange={(e) => setDateRangeFilter({...dateRangeFilter, endDate: e.target.value})}
                />
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3">
              <button
                onClick={handleClearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Сбросить фильтры
              </button>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : priceTags.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-gray-500 mb-2">Ценники не найдены</p>
              <p className="text-gray-400 text-sm">Попробуйте изменить фильтры или добавить новые ценники</p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                          header.column.getCanSort() ? 'cursor-pointer select-none hover:text-gray-700' : ''
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <span className="text-blue-600">&#9650;</span>,
                            desc: <span className="text-blue-600">&#9660;</span>,
                          }[header.column.getIsSorted()] ?? (
                            header.column.getCanSort() ? <span className="text-gray-300">&#9650;&#9660;</span> : null
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                {totalCount > 0 ? (
                  <>
                    Показано {Math.min((page - 1) * pageSize + 1, totalCount)} - {Math.min(page * pageSize, totalCount)} из {totalCount} записей
                  </>
                ) : (
                  <>Нет записей</>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-500">Строк:</span>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1 || loading}
                className={`px-2 py-1 rounded-md text-sm ${page === 1 || loading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                &laquo;
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1 || loading}
                className={`px-2 py-1 rounded-md text-sm ${page === 1 || loading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                &lsaquo;
              </button>
              {(() => {
                const pages = [];
                let start = Math.max(1, page - 2);
                let end = Math.min(totalPages, start + 4);
                if (end - start < 4) start = Math.max(1, end - 4);

                if (start > 1) {
                  pages.push(
                    <button key={1} onClick={() => setPage(1)} className="px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100">1</button>
                  );
                  if (start > 2) pages.push(<span key="dots-start" className="px-1 text-gray-400">...</span>);
                }

                for (let i = start; i <= end; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setPage(i)}
                      disabled={loading}
                      className={`px-3 py-1 rounded-md text-sm ${
                        i === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {i}
                    </button>
                  );
                }

                if (end < totalPages) {
                  if (end < totalPages - 1) pages.push(<span key="dots-end" className="px-1 text-gray-400">...</span>);
                  pages.push(
                    <button key={totalPages} onClick={() => setPage(totalPages)} className="px-3 py-1 rounded-md text-sm text-gray-600 hover:bg-gray-100">{totalPages}</button>
                  );
                }

                return pages;
              })()}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages || loading}
                className={`px-2 py-1 rounded-md text-sm ${page === totalPages || loading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                &rsaquo;
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || loading}
                className={`px-2 py-1 rounded-md text-sm ${page === totalPages || loading ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Модалка просмотра фото */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowImageModal(false)}
          ></div>
          <div className="relative bg-white rounded-lg shadow-lg p-4 max-w-5xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Просмотр изображения ценника</h3>
              <button
                onClick={() => setShowImageModal(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex justify-center">
              <img
                src={selectedImageUrl || selectedImage?.filePath || selectedImage?.url || selectedImage}
                alt="Ценник"
                className="max-h-[80vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceTagsPage;
